import { PureQueryOptions } from "@apollo/client";
import { useDateFormatting } from "@notemeal/shared/ui/contexts/useDateFormatting";
import Loading from "@notemeal/shared/ui/global/Loading";
import TablePage from "apps/web/src/components/universal/TablePage";
import { RefetchQueriesContextProvider } from "apps/web/src/contexts/RefetchQueries";
import { NamedTagForSelectionFragment, PositionFragment, TeamMealMenuPreviewFragment, useMealMenuAttendeesQuery } from "apps/web/src/types";
import { getRowsForTablePage, useOffsetPagination } from "apps/web/src/utils/pagination";
import { MealMenuAttendanceTableHeaderRow, MealMenuAttendanceTableRow } from "apps/web/src/views/MenuAttendance/AttendanceRow";
import { exportAttendanceToExcel } from "apps/web/src/views/MenuAttendance/Export";
import { MealMenuAttendanceTableToolbar } from "apps/web/src/views/MenuAttendance/Toolbar";
import React, { useState } from "react";
import { useParams } from "react-router-dom-v5-compat";
import { MealMenuAttendeesToAttendanceRows, MenuAttendanceRowInput } from "./utils";

interface MenuAttendancePageProps {
  teams: { id: string; name: string }[];
  refetchQueries: PureQueryOptions[];
  mealMenuName: string;
}

export const MenuAttendancePage = ({ teams, refetchQueries, mealMenuName }: MenuAttendancePageProps) => {
  const { mealMenuId: maybeMealMenuId } = useParams();
  const mealMenuId = maybeMealMenuId || "";
  const { formatHubDisplayDate } = useDateFormatting();

  const [selectedTeams, setSelectedTeams] = useState<readonly TeamMealMenuPreviewFragment[]>([]);
  const [selectedPositions, setSelectedPositions] = useState<readonly PositionFragment[]>([]);
  const [namedTagsForQuery, setNamedTagsForQuery] = useState<NamedTagForSelectionFragment[]>([]);
  const [checkedInForQuery, setCheckedInForQuery] = useState<boolean | null>(null);
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);
  const [sortOrderAscending, setSortOrderAscending] = useState<boolean | null>(false);
  const [searchText, setSearchText] = useState("");

  // tags ids will trigger a refetch but acceptable for MVP
  const tagIds = namedTagsForQuery.map(({ tag: { id } }) => id);

  const paginationHooks = useOffsetPagination();
  const { page, limit, resetOffset } = paginationHooks;

  const { data: mealMenuAttendanceData, loading: mealMenuAttendanceLoading } = useMealMenuAttendeesQuery({
    variables: {
      mealMenuId,
      tagIds,
      priorityTagIds: tagIds,
    },
  });

  const handleChangeSearchText = (newSearchText: string) => {
    setSearchText(newSearchText);
    resetOffset();
  };

  const handleChangePositions = (newSelectedPositions: readonly PositionFragment[]) => {
    setSelectedPositions([...newSelectedPositions]);
    resetOffset();
  };

  const handleChangeTeams = (newSelectedTeams: readonly TeamMealMenuPreviewFragment[]) => {
    setSelectedTeams([...newSelectedTeams]);
    resetOffset();
  };

  const handleChangeCheckedIn = (newCheckedIn: boolean | null) => {
    setCheckedInForQuery(newCheckedIn);
    resetOffset();
  };

  const handleChangeHasAccess = (newHasAccess: boolean | null) => {
    setHasAccess(newHasAccess);
    resetOffset();
  };

  const handleChangeNamedTags = (newTags: NamedTagForSelectionFragment[]) => {
    setNamedTagsForQuery([...newTags]);
    resetOffset();
  };

  const handleSortOrder = () => {
    const newSortOrder = sortOrderAscending === null ? true : !sortOrderAscending;
    setSortOrderAscending(newSortOrder);
    resetOffset();
  };

  if (mealMenuAttendanceLoading) {
    return <Loading />;
  }

  const allPositions: PositionFragment[] = mealMenuAttendanceData
    ? mealMenuAttendanceData.mealMenuAttendees.notemealAttendees.flatMap(a => (a.athlete.position ? [a.athlete.position] : []))
    : [];

  const sortRows = (a: MenuAttendanceRowInput, b: MenuAttendanceRowInput) => {
    if (sortOrderAscending === null) {
      const valA = a.teamName;
      const valB = b.teamName;
      return valA > valB ? 1 : -1;
    } else {
      // purely for hub checkin display dates
      // sortByFn could possibly work but hub's checkin date makes it arguable just as messy
      const valA = a.checkInDisplayDate ? new Date(a.checkInDisplayDate.substring(0, 19)).getTime() : 0;
      const valB = b.checkInDisplayDate ? new Date(b.checkInDisplayDate.substring(0, 19)).getTime() : 0;
      return sortOrderAscending ? valA - valB : valB - valA;
    }
  };

  const allRows = mealMenuAttendanceData
    ? MealMenuAttendeesToAttendanceRows({
        notemealAttendees: [...mealMenuAttendanceData.mealMenuAttendees.notemealAttendees],
        nonNotemealAttendees: [...mealMenuAttendanceData.mealMenuAttendees.nonNotemealAttendees],
      })
    : [];

  const filteredTeamRows = selectedTeams.length > 0 ? allRows.filter(r => selectedTeams.map(t => t.id).includes(r.teamId)) : allRows;
  const filteredHasAccessRows = hasAccess !== null ? filteredTeamRows.filter(r => r.hasAccess === hasAccess) : filteredTeamRows;
  const filteredCheckedInRows =
    checkedInForQuery !== null ? filteredHasAccessRows.filter(r => r.checkedIn === checkedInForQuery) : filteredHasAccessRows;
  const filteredPositionRows =
    selectedPositions.length > 0
      ? filteredCheckedInRows.filter(r => selectedPositions.map(p => p.id).includes(r.positionId))
      : filteredCheckedInRows;
  const filteredQueryRows =
    searchText.length > 0 ? filteredPositionRows.filter(r => r.fullName.includes(searchText)) : filteredPositionRows;
  const rows = filteredQueryRows.sort(sortRows);

  const exportData = () => {
    const rowsWithFormattedDateString = rows.map(row => ({
      ...row,
      formattedCheckInDate: row.checkInDisplayDate ? formatHubDisplayDate(row.checkInDisplayDate) : "",
    }));
    exportAttendanceToExcel({ fileName: `${mealMenuName}_Attendance`, rows: rowsWithFormattedDateString });
  };

  const pageRows = getRowsForTablePage({ rows, limit, page });

  return (
    <RefetchQueriesContextProvider refetchQueries={refetchQueries}>
      <TablePage
        tableHeaderRow={<MealMenuAttendanceTableHeaderRow sortOrderAscending={sortOrderAscending} onChangeSortOrder={handleSortOrder} />}
        header={
          <MealMenuAttendanceTableToolbar
            selectedTeams={selectedTeams}
            onChangeTeams={handleChangeTeams}
            onChangeCheckedIn={handleChangeCheckedIn}
            searchText={searchText}
            onChangeSearchText={handleChangeSearchText}
            hasAccess={hasAccess}
            onChangeHasAccess={handleChangeHasAccess}
            selectNamedTags={namedTagsForQuery}
            onChangeNamedTags={handleChangeNamedTags}
            checkedIn={checkedInForQuery}
            teams={teams}
            positions={[...new Set(allPositions)]}
            selectedPositions={selectedPositions}
            onChangePosition={handleChangePositions}
            exportData={exportData}
          />
        }
        paginationHooks={paginationHooks}
        total={rows.length}
        tableBodyRows={pageRows.map(row => (
          <MealMenuAttendanceTableRow input={row} />
        ))}
        loading={false}
      />
    </RefetchQueriesContextProvider>
  );
};
